import { createSlice, createAsyncThunk, AsyncThunk, AnyAction } from '@reduxjs/toolkit';
import type { AppDispatch } from '@nextgen-web-framework/core';
import type {
  CreateMyShopPayloadType,
  InitialMyShopState,
  MyShopResponse,
  ProjectionReqPayloadType,
  Forbidden,
} from '../../types/myshopTypes';
import { MyShop } from '../models';
import { myshopApi } from '../requests';

const initialState: InitialMyShopState = {
  isLoading: false,
  hasError: false,
  error: {},
  selectedBannerImage: '',
  selectedProfileImage: '',
  updatedBannerImage: '',
  updatedProfileImage: '',
  selectedTnc: false,
  shop: {},
  getMyShopStatusError: false,
  createMyShopStatusSuccess: false,
  createMyShopStatusError: false,
  updateMyShopStatusSuccess: false,
  updateMyShopStatusError: false,
  updateFeatureProductSuccess: false,
};

export const getMyShopSliceReq = createAsyncThunk(
  'commerceMyshop/getMyShop',
  async ({ projections, shopCodeUrl }: ProjectionReqPayloadType) => {
    const res = await myshopApi.getMyShop({
      projections,
      shopCodeUrl,
    });

    return res;
  },
);

export const createMyShopSliceReq = createAsyncThunk(
  'commerceMyshop/createMyShop',
  ({ payload }: CreateMyShopPayloadType) =>
    myshopApi.createMyShop({
      payload,
    }),
);

export const updateMyShopSliceReq = createAsyncThunk(
  'commerceMyshop/updateMyShop',
  ({ payload }: CreateMyShopPayloadType) =>
    myshopApi.updateMyShop({
      payload,
    }),
);

type GenericAsyncThunk = AsyncThunk<
  MyShopResponse,
  unknown,
  {
    // Optional fields for defining thunkApi field types
    dispatch: AppDispatch; // this is store.dispatch
    state: InitialMyShopState; // this is
    extra: {
      MYSHOP_BASE_API_URL: string; // eslint-disable-line
    };
  }
>;
type PendingAction = ReturnType<GenericAsyncThunk['pending']>;
type RejectedAction = ReturnType<GenericAsyncThunk['rejected']>;
type FulfilledAction = ReturnType<GenericAsyncThunk['fulfilled']>;

function isPendingGenericAction(action: AnyAction): action is PendingAction {
  return action.type.endsWith('/pending');
}
function isFulfilledAction(action: AnyAction): action is FulfilledAction {
  return action.type.endsWith('/fulfilled');
}

function isRejectedAction(action: AnyAction): action is RejectedAction {
  return action.type.startsWith('commerceMyShop') && action.type.endsWith('/rejected');
}

export const myShopSlice = createSlice({
  name: 'commerceMyshop',
  initialState,
  reducers: {
    resetMyshopSlice: () => initialState,
    updateSelectedBanner: (state, action) => {
      state.selectedBannerImage = action.payload;
      state.updatedBannerImage = action.payload;
    },
    updateSelectedProfile: (state, action) => {
      state.selectedProfileImage = action.payload;
      state.updatedProfileImage = action.payload;
    },
    resetProfileBannerImage: (state) => {
      state.updatedBannerImage = '';
      state.updatedProfileImage = '';
    },
    updateSelectedTnc: (state, action) => {
      state.selectedTnc = action.payload;
    },
    updateMyListCreateState: (state) => {
      state.updateMyShopStatusError = false;
      state.updateMyShopStatusSuccess = false;
      state.updateFeatureProductSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getMyShopSliceReq.fulfilled, (state, action) => {
      state.isLoading = false;
      state.hasError = false;
      state.shop = action.payload as MyShop;
    });
    builder.addCase(getMyShopSliceReq.rejected, (state, action) => {
      state.isLoading = false;
      state.hasError = true;
      state.error = action as Forbidden;
      state.getMyShopStatusError = true;
    });
    builder.addCase(createMyShopSliceReq.fulfilled, (state, action) => {
      state.isLoading = false;
      state.hasError = false;
      state.shop = action.payload as MyShop;
      state.createMyShopStatusSuccess = true;
    });

    builder.addCase(createMyShopSliceReq.rejected, (state, action) => {
      state.isLoading = false;
      state.hasError = false;
      state.error = action as Forbidden;
      state.createMyShopStatusError = true;
    });
    builder.addCase(updateMyShopSliceReq.fulfilled, (state, action) => {
      state.isLoading = false;
      state.hasError = false;
      const s = state.shop;
      const p = action.payload as MyShop;
      if (
        p.featureProducts &&
        (s.featureProducts === undefined || JSON.stringify(s.featureProducts) !== JSON.stringify(p.featureProducts))
      ) {
        state.updateFeatureProductSuccess = true;
      } else {
        state.updateMyShopStatusSuccess = true;
      }
      state.shop = action.payload as MyShop;
    });

    builder.addCase(updateMyShopSliceReq.rejected, (state, action) => {
      state.isLoading = false;
      state.hasError = true;
      state.error = action as Forbidden;
      state.updateMyShopStatusError = true;
    });

    /**
     * Common reducers
     */
    builder.addMatcher(isRejectedAction, (state: InitialMyShopState, action) => {
      state.isLoading = false;
      state.hasError = true;
      state.error = action as Forbidden;
    });
    builder.addMatcher(isFulfilledAction, (state: InitialMyShopState) => {
      state.isLoading = false;
      state.hasError = false;
    });
    builder.addMatcher(isPendingGenericAction, (state: InitialMyShopState) => {
      state.isLoading = true;
      state.hasError = false;
    });
  },
});

export const {
  resetMyshopSlice,
  updateSelectedBanner,
  updateSelectedProfile,
  updateSelectedTnc,
  updateMyListCreateState,
  resetProfileBannerImage,
} = myShopSlice.actions;
